import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import BasicLayout from '@/components/common/layout/basic';
import { Row, Col, Card, Table, Tag, Descriptions, Modal, Typography, Spin } from 'antd';
import { SwapLeftOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useEstimateDetailsData } from '@/hooks/useEstimateDetailsData';
import AttachmentCell from '@/components/user/site-details/AttachmentCell';
import FeedbackTable from '@/components/user/site-details/FeedbackTable';
import { t } from '@/utils/translator';
import i18n from '@/i18n';
import dayjs from 'dayjs';
import {
  StatusColor,
  CycleStatus,
  ProjectStatusCode,
  ProjectStatusCodeColor,
} from '@constants/status';
import { STYLE_CONSTANTS } from '@constants/siteData';
import { getPreferredTimezone, formatDateRange } from '@/utils/dateFormatter';

function ClientDashboardEstimateDetailed() {
  const [searchParams] = useSearchParams();
  const project_code = searchParams.get('project_code');
  const { estimate_id } = useParams();
  const userTimezone = getPreferredTimezone();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [AttachmentColumnWidth, setAttachmentColumnWidth] = useState(0);

  const {
    loading,
    siteLoading,
    estimateInfo,
    estimateSites,
    activeSite,
    activeSiteKey,
    siteCycles,
    requiredDocuments,
    optionalDocuments,
    changeActiveSite,
  } = useEstimateDetailsData(estimate_id);

  const hasAnyFeedback = (cycle) => {
    const hasCycleFeedback = cycle.cycle_feedback_comment !== null;
    const hasAttachmentFeedback = Object.values(cycle.attachments).some((attachmentList) =>
      attachmentList.some((item) => item.feedback_id !== null)
    );

    return hasCycleFeedback || hasAttachmentFeedback;
  };

  useEffect(() => {
    const _AttachmentColumnWidth =
      requiredDocuments.length + optionalDocuments.length
        ? `${70 / (requiredDocuments.length + optionalDocuments.length)}%`
        : !optionalDocuments.length
          ? `${70 / requiredDocuments.length}%`
          : `${70 / optionalDocuments.length}%`;

    setAttachmentColumnWidth(_AttachmentColumnWidth);
  }, [requiredDocuments, optionalDocuments, setAttachmentColumnWidth]);

  function SiteCycleInfoTable(estimateSite) {
    if (!estimateSite) return null;
    return (
      <>
        <Typography.Title level={4} className="text-gray-9 mb-3">
          {t('사업장 정보')}
        </Typography.Title>
        <Descriptions
          column={5}
          size="small"
          className="border mb-5"
          labelStyle={STYLE_CONSTANTS.DESC_STYLE_HEADER}
          contentStyle={STYLE_CONSTANTS.DESC_STYLE_BODY}
          layout="vertical"
          bordered
        >
          <Descriptions.Item label={t('소속 기업')}>
            {estimateInfo.company['company_name']}
          </Descriptions.Item>
          <Descriptions.Item label={t('사업장명')}>{estimateSite.site_name}</Descriptions.Item>
          <Descriptions.Item label={t('사업장 국가')}>
            {estimateSite.site_country}
          </Descriptions.Item>
          <Descriptions.Item label={t('사업장 주소')}>
            {estimateSite.site_location}
          </Descriptions.Item>
          <Descriptions.Item label={t('HL 만도 거래법인')}>
            {estimateSite?.site_clients?.join(', ')}
          </Descriptions.Item>
        </Descriptions>

        <div className="d-flex justify-content-between">
          <Typography.Title level={4} className="text-gray-9 mb-3">
            {t('산정 진행상태')}
          </Typography.Title>
          <div className="">
            <span className="fw-medium me-2" style={{ fontSize: '15px' }}>
              {t('회차별 상태')}:
            </span>
            {Object.entries(StatusColor).map(([state]) => (
              <Tag color={StatusColor[state]} key={state}>
                {t(CycleStatus[state])}
              </Tag>
            ))}
          </div>
        </div>

        <Table
          dataSource={siteCycles}
          rowKey="cycle_index"
          pagination={{ position: ['none', 'none'] }}
          bordered
          style={{ width: '100%' }}
          tableLayout="fixed"
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
            expandedRowRender: (record) => {
              const feedbackRows = (Object.values(record.attachments).flat() || [])
                .filter((attachment) => attachment?.feedback_id && attachment?.feedback_comment)
                .map((attachment) => ({
                  key: attachment?.feedback_id,
                  attachment_type: attachment?.[`document_type_name_${i18n.language}`],
                  filename: attachment?.filename || '-',
                  feedback: attachment?.feedback_comment,
                }));

              return <FeedbackTable row={record} feedbacks={feedbackRows} />;
            },
            rowExpandable: (record) => hasAnyFeedback(record),
            showExpandColumn: true,
          }}
        >
          <Table.Column
            title={<span className={clsx('fw-medium', 'fs-6')}>{t('회차')}</span>}
            dataIndex="cycle_index"
            key="cycle_index"
            align="center"
            width={'10%'}
            render={(cycle_index) => <span style={{ fontSize: '15px' }}>{cycle_index}</span>}
          />
          <Table.Column
            title={<span className={clsx('fw-medium', 'fs-6')}>{t('상태')}</span>}
            dataIndex="cycle_status"
            key="cycle_status"
            align="center"
            width={'10%'}
            render={(_, record) => (
              <Tag color={StatusColor[record.cycle_status]} key={record.cycle_status}>
                {t(CycleStatus[record.cycle_status])}
              </Tag>
            )}
          />
          {requiredDocuments.length > 0 && (
            <Table.ColumnGroup
              title={<span className={clsx('fw-medium', 'fs-6')}>{t('필수 증빙서류')}</span>}
              className="text-center"
            >
              {requiredDocuments.map((document) => (
                <Table.Column
                  key={document?.document_type?.type_code}
                  title={
                    <span className={clsx('fw-medium', '')}>
                      {document['document_type'][`type_name_${i18n.language}`]}
                    </span>
                  }
                  dataIndex={document?.document_type?.type_code}
                  width={AttachmentColumnWidth}
                  align="center"
                  ellipsis
                  render={(_, record) => {
                    return (
                      <AttachmentCell
                        record={record}
                        documentType={document?.document_type?.type_code}
                        attachments={record.attachments[document?.document_type?.type_code]}
                        // refreshCycleData={fetchEstimateCycles}
                      />
                    );
                  }}
                ></Table.Column>
                // <Table.Column
                //   key={document.id || document.document_type.type_code}
                //   title={
                //     <span className={clsx('fw-medium', '')}>
                //       {document['document_type'][`type_name_${i18n.language}`] ||
                //         document.document_type.type_name_ko ||
                //         document.document_type.type_code}
                //     </span>
                //   }
                //   dataIndex={document.document_type.type_code || document.id}
                //   align="center"
                //   render={(_, record) => {
                //     if (
                //       record.attachments &&
                //       record.attachments[document.document_type.type_code] &&
                //       record.attachments[document.document_type.type_code].length
                //     ) {
                //       return record.attachments[document.document_type.type_code].map(
                //         (attachment) => (
                //           <div className="w-100 text-center mb-1" key={attachment.id}>
                //             <div className="d-inline-flex text-ellipsis" style={{ width: '90%' }}>
                //               <a
                //                 href={`${import.meta.env.VITE_STATIC_URL}/${attachment?.s3_key || ''}`}
                //                 className="text-truncate"
                //                 target="_blank"
                //                 title={attachment.filename || t('첨부파일')}
                //                 download
                //               >
                //                 {attachment.filename}
                //               </a>
                //             </div>
                //           </div>
                //         )
                //       );
                //     } else {
                //       return <div> ㅡ </div>;
                //     }
                //   }}
                // ></Table.Column>
              ))}
            </Table.ColumnGroup>
          )}
          {optionalDocuments.length > 0 && (
            <Table.ColumnGroup
              title={<span className={clsx('fw-medium', 'fs-6')}>{t('증빙서류')}</span>}
              className="text-center"
            >
              {optionalDocuments.map((document) => (
                <Table.Column
                  key={document.id || document.document_type.type_code}
                  title={
                    <span className={clsx('fw-medium', '')}>
                      {document['document_type'][`type_name_${i18n.language}`] ||
                        document.document_type.type_name_ko ||
                        document.document_type.type_code}
                    </span>
                  }
                  dataIndex={document.document_type.type_code || document.id}
                  align="center"
                  render={(_, record) => {
                    if (record.attachments[document.document_type.type_code]) {
                      return record.attachments[document.document_type.type_code].map(
                        (attachment) => (
                          <div className="w-100 text-center mb-1" key={attachment.id}>
                            <div className="d-inline-flex text-ellipsis" style={{ width: '90%' }}>
                              <a
                                href={`${import.meta.env.VITE_STATIC_URL}/${attachment?.s3_key || ''}`}
                                className="text-truncate"
                                target="_blank"
                                title={attachment.filename || t('첨부파일')}
                                download
                              >
                                {attachment.filename}
                              </a>
                            </div>
                          </div>
                        )
                      );
                    } else {
                      return <div> ㅡ </div>;
                    }
                  }}
                ></Table.Column>
              ))}
            </Table.ColumnGroup>
          )}
        </Table>
      </>
    );
  }

  const renderRemainingDays = () => {
    if (!estimateInfo) return '-';
    const targetDate = dayjs(estimateInfo.project['end_date']);
    const now = dayjs();
    const diffInMs = targetDate.diff(now);
    if (diffInMs <= 0)
      return i18n.language === 'ko' ? '만료됨' : i18n.language === 'en' ? 'Expired' : '已过期';
    const totalDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const remainingMs = diffInMs % (1000 * 60 * 60 * 24);
    const totalHours = Math.floor(remainingMs / (1000 * 60 * 60));
    return (
      <>
        {i18n.language === 'ko' && `${totalDays}일 ${totalHours} 시간`}
        {i18n.language === 'en' && `${totalDays} days ${totalHours} hours`}
        {i18n.language === 'zh' && `${totalDays}天 ${totalHours} 小时`}
      </>
    );
  };

  return (
    <BasicLayout body_classname="bg-light">
      <Modal open={loading} footer={null} centered closable={false}>
        <div className="text-center">
          <div>프로젝트 진행현황을 조회중입니다.</div>
          <div>잠시만 기다려주세요.</div>
        </div>
      </Modal>

      {!loading && estimateInfo && (
        <>
          <Row className="mb-4">
            <Col xs={24} xl={{ span: 22, offset: 1 }}>
              <div className="text-start">
                <Link
                  className="text-gray-7 hover-text-blue-8 fs-6"
                  to={`/client/dashboard?project_code=${project_code}`}
                >
                  <SwapLeftOutlined className="me-2" />
                  프로젝트 진행현황으로 돌아가기
                </Link>
              </div>
            </Col>
          </Row>

          <Row gutter={[24, 24]} className="mb-5">
            <Col xs={24} xl={{ span: 22, offset: 1 }}>
              <Card className="shadow-sm">
                <Typography.Title level={3} className="text-gray-9 mb-3">
                  {t('프로젝트 정보')} / {t('협력사')} {t('기업 정보')}
                </Typography.Title>
                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    <Descriptions
                      column={6}
                      size="small"
                      className="border"
                      labelStyle={STYLE_CONSTANTS.DESC_STYLE_HEADER}
                      contentStyle={STYLE_CONSTANTS.DESC_STYLE_BODY}
                      layout="vertical"
                      bordered
                    >
                      <Descriptions.Item label={`${t('프로젝트명')} / ${t('프로젝트 코드')}`}>
                        {estimateInfo?.project?.[`project_name_${i18n.language}`]}
                        <br />({estimateInfo.project.project_code})
                      </Descriptions.Item>
                      <Descriptions.Item label={`${t('프로젝트')} ${t('기간')}`}>
                        {formatDateRange(
                          estimateInfo.project['start_date'],
                          estimateInfo.project['end_date'],
                          userTimezone
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('남은 기간')}>
                        {renderRemainingDays()}
                      </Descriptions.Item>
                      <Descriptions.Item label={`${t('협력사')} ${t('기업명')}`}>
                        {estimateInfo.company['company_name']}
                      </Descriptions.Item>
                      <Descriptions.Item label={`${t('프로젝트')} ${t('참여유무')}`}>
                        <Tag
                          color={
                            estimateInfo.vendor_code.used
                              ? ProjectStatusCodeColor['STARTED']
                              : ProjectStatusCodeColor['NOT_STARTED']
                          }
                          style={{ fontSize: '12px', lineHeight: 2.1 }}
                        >
                          {t(estimateInfo.vendor_code.used ? '참여중' : '미신청')}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label={`${t('산정 진행상태')}`}>
                        <Tag
                          color={ProjectStatusCodeColor[estimateInfo.estimate_info_status]}
                          style={{ fontSize: '12px', lineHeight: 2.1 }}
                        >
                          {t(ProjectStatusCode[estimateInfo.estimate_info_status])}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xs={24} xl={{ span: 22, offset: 1 }}>
              <Card bodyStyle={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
                <Row>
                  <Col xs={24}>
                    <Typography.Title level={3} className="text-gray-9 px-4 mb-3">
                      {t('사업장')} {t('목록')}
                    </Typography.Title>
                  </Col>
                </Row>
                {estimateSites.length === 0 && (
                  <Row>
                    <Col xs={24}>
                      <div className="text-center bg-gray-1 p-5 m-2">
                        <Typography.Title level={3} className="fw-medium text-gray-4 mb-0">
                          추가된 사업장이 없습니다
                        </Typography.Title>
                      </div>
                    </Col>
                  </Row>
                )}
                {estimateSites.length > 0 && (
                  <Card
                    className="border-0"
                    onTabChange={(key) => changeActiveSite(key)}
                    tabList={estimateSites.map(({ estimate_site_info_id, site_name }) => ({
                      id: estimate_site_info_id,
                      key: estimate_site_info_id,
                      tab: site_name,
                    }))}
                    activeTabKey={activeSiteKey}
                  >
                    {siteLoading && (
                      <div className="d-flex align-items-center justify-content-center bg-light p-4">
                        <Spin size="large" className="me-4" />
                        <div className="text-gray-8">
                          <p className="fs-6 mb-0">사업장 정보를 조회중입니다.</p>
                          <p className="fs-6 mb-0">잠시만 기다려주세요</p>
                        </div>
                      </div>
                    )}
                    {!siteLoading && (
                      <Row>
                        <Col xs={24}>{SiteCycleInfoTable(activeSite)}</Col>
                      </Row>
                    )}
                  </Card>
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BasicLayout>
  );
}

export default ClientDashboardEstimateDetailed;
